import { showDialog, showDialogWithData } from "../../../redux/guest-slices/other.slice";
import { handleRetrieveWebinars } from "../../../redux/public-slices/webinar.slice";
import { getAllSpecializationsReq } from "../../../redux/guest-slices/common.slice";
import TablePagination from "../../../components/common/TablePagination";
import { Fragment, lazy, Suspense, useEffect, useState } from "react";
import { CREATE_BOOKING, GUEST_LOGIN } from "../../../utils/keywords";
import SectionSkeleton from "../../../components/ui/SectionSkeleton";
import { checkUserType, FILE_PATH_URI } from "../../../utils/helper";
import InputFieldB from "../../../components/ui/InputFieldB";
import ContentCard from "../../../components/common/Content";
import { useDispatch, useSelector } from "react-redux";
import Images from "../../../assets/js/Images";
import Icons from "../../../assets/js/Icons";
import { useRef } from "react";

import {
  Alert,
  Box,
  Container,
  Grid2 as Grid,
  Skeleton,
  TextField,
  Typography,
  Autocomplete,
  IconButton,
} from "@mui/material";

const TopBannerA = lazy(() => import("../../../components/common/TopBannerA"));


const AllWebinars = () => {
  const topicRef = useRef(null);
  const dispatch = useDispatch();
  const searchRef = useRef(null);
  const endDateRef = useRef(null);
  const startDateRef = useRef(null);
  const specialistRef = useRef(null);
  const [data, setData] = useState({});
  const [topics, setTopics] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const { loadings } = useSelector((state) => state.otherSlice);

  // Date input type
  const [startDateType, setStartDateType] = useState("text");
  const [endDateType, setEndDateType] = useState("text");

  // Open native date picker
  const openDatePicker = (ref, setType) => {
    setType("date");

    setTimeout(() => {
      if (ref.current) {
        ref.current.focus();

        if (typeof ref.current.showPicker === "function") {
          try {
            ref.current.showPicker();
          } catch (error) {
            // Browser handles date picker automatically
          }
        }
      }
    }, 100);
  };

  // Change back to text if no date is selected
  const handleDateBlur = (ref, setType) => {
    if (!ref.current?.value) {
      setType("text");
    }
  };

  const handleRetrieveAll = async (filters) => {
    const response = await dispatch(
      handleRetrieveWebinars({
        limit: 10,
        type: "upcoming",
        currentPage: filters?.currentPage || null,
        search: searchRef?.current?.value || null,
        specialistName: specialistRef?.current?.name || null,
        topicId: topicRef?.current?._id,
        startDate: startDateRef?.current?.value || null,
        endDate: endDateRef?.current?.value || null,
      })
    ).unwrap();
    setData(response);

    if (response?.records?.length > 0 && specialists.length === 0) {
      const uniqueSpecialists = response.records
        .filter((record) => record.user)
        .map((record) => ({
          _id: record.user._id,
          name: record.user.fullName,
        }))
        .filter(
          (specialist, index, self) =>
            index === self.findIndex((s) => s._id === specialist._id)
        );

      setSpecialists(uniqueSpecialists);
    }
  };
  const handleDateSearch = () => {
    const startDate = startDateRef?.current?.value;
    const endDate = endDateRef?.current?.value;

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("Start date cannot be later than end date");
      return;
    }

    handleRetrieveAll();
  };
  const handleGetAll = (filters) => {
    dispatch(
      getAllSpecializationsReq({
        limit: filters?.limit || 100,
      })
    )
      .unwrap()
      .then((data) => {
        setTopics(data);
      })
      .catch(() => { });
  };

  const handleLogin = () => {
    dispatch(showDialog(GUEST_LOGIN));
  };

  const handleBooking = (webinarId) => {
    if (checkUserType()) {
      dispatch(
        showDialogWithData({
          type: CREATE_BOOKING,
          data: { webinarId },
        })
      );
    } else {
      handleLogin();
    }
  };

  useEffect(() => {
    handleGetAll();
    handleRetrieveAll();
    // eslint-disable-next-line
  }, []);

  return (
    <Fragment>
      <Suspense fallback={<SectionSkeleton />}>
        <TopBannerA
          heading="Webinars"
          currentPage="Webinars"
          bannerImg={Images?.blogs}
        />
      </Suspense>

      <Box sx={{ py: { xs: 5, lg: 10 }, bgcolor: "#f1f1f1" }}>
        <Container maxWidth={false} fixed>
          <Typography
            fontSize={20}
            sx={{
              mb: "20px",
            }}
            variant="h1"
            fontWeight="600"
            color="other.color"
          >
            Learn. Reflect. Grow. Join our expert-led webinars and unlock
            practical tips to support your emotional wellness journey — anytime,
            anywhere.
          </Typography>
          <Grid container spacing={4} justifyContent="" mb={5}>
            <Grid>
              <Box
  sx={{
    gap: 1,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  }}
>
  <InputFieldB
    type={startDateType}
    name="startDate"
    variant="outlined"
    placeholder="Start Date"
    inputRef={startDateRef}
    onFocus={() =>
      openDatePicker(startDateRef, setStartDateType)
    }
    onBlur={() =>
      handleDateBlur(startDateRef, setStartDateType)
    }
    sx={{
      width: "180px",
      minWidth: "180px",
      maxWidth: "180px",
    }}
  />

  <InputFieldB
    type={endDateType}
    name="endDate"
    variant="outlined"
    placeholder="End Date"
    inputRef={endDateRef}
    onFocus={() =>
      openDatePicker(endDateRef, setEndDateType)
    }
    onBlur={() =>
      handleDateBlur(endDateRef, setEndDateType)
    }
    sx={{
      width: "180px",
      minWidth: "180px",
      maxWidth: "180px",
    }}
  />

  <Box>
    <IconButton
      variant="contained"
      color="primary"
      onClick={handleDateSearch}
    >
      <Icons.Checkmark />
    </IconButton>
  </Box>
</Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
              <Autocomplete
                size="small"
                id="topics"
                options={topics || []}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select topic" />
                )}
                onChange={(_, value) => {
                  setTimeout(() => {
                    topicRef.current = value;
                    handleRetrieveAll();
                  }, 0);
                }}
              />
            </Grid>

            {/* Specialists Autocomplete */}
            <Grid size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
              <Autocomplete
                size="small"
                id="specialists"
                options={specialists || []}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select specialist" />
                )}
                onChange={(_, value) => {
                  setTimeout(() => {
                    specialistRef.current = value;
                    handleRetrieveAll();
                  }, 0);
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={5} sx={{ height: "100%" }}>
            {loadings?.retrieveAll ? (
              new Array(4).fill().map((_, i) => (
                <Grid key={i} size={{ xs: 12, md: 6, lg: 4 }}>
                  <Skeleton
                    sx={{
                      width: "100%",
                      transform: "none",
                      aspectRatio: "1/0.5",
                    }}
                  />
                </Grid>
              ))
            ) : data?.records?.length === 0 ? (
              <Grid size={12}>
                <Alert
                  sx={{
                    fontSize: "20px",
                    fontWeight: "500",
                    alignItems: "center",
                    fontFamily: "Poppins",
                    ".MuiAlert-message": { p: "0" },
                    ".MuiAlert-icon": { fontSize: "28px" },
                  }}
                  color="error"
                  severity="error"
                >
                  No Records Found!
                </Alert>
              </Grid>
            ) : (
              data?.records?.map((element, i) => (
                <Grid key={i} size={{ xs: 12, md: 6, lg: 4 }}>
                  <ContentCard
                    type="webinar"
                    id={element._id}
                    title={element.details.title}
                    image={FILE_PATH_URI + element.details.coverImage.path}
                    date={element.schedule.startTime}
                    onClickButton={() => handleBooking(element._id)}
                    buttonText={element.isBooked ? "Booked" : "Book now"}
                    buttonDisabled={element.isBooked}
                  />
                </Grid>
              ))
            )}
          </Grid>

          <TablePagination
            pagination={data?.pagination}
            length={data?.records?.length}
            loading={loadings?.retrieveAll}
            cb={handleRetrieveAll}
          />
        </Container>
      </Box>
    </Fragment>
  );
};

export default AllWebinars;
